import { useState, useRef, useEffect, useMemo } from 'react'
import * as d3 from 'd3'
import { feature } from 'topojson-client'
import landTopo from 'world-atlas/land-110m.json'
// 50m for the clip shapes: the 110m set drops small territories
// (Hong Kong, Singapore, ...), whose zones would fall back to an
// unclipped band. The land backdrop stays at 110m — it's just texture.
import countriesTopo from 'world-atlas/countries-50m.json'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PieChartIcon from '@mui/icons-material/PieChart'
import { useTheme } from '@mui/material/styles'
import TIMEZONE_COORDS, { TIMEZONE_ALIASES } from './timezoneGeo'
import { PERSONAS } from './cohorts'

// One hour of longitude: the highlight reads as "a timezone", not a pin.
const BAND_DEGREES = 15
// Bands are clipped to the countries that use the zone, so they can run
// (nearly) pole to pole and let the country outline decide the extent.
const BAND_SOUTH = -89
const BAND_NORTH = 86
// The preview map keeps simple unclipped bands over these latitudes.
const PREVIEW_SOUTH = -56
const PREVIEW_NORTH = 78
// A zone whose countries are missing even from the 50m atlas gets a
// local patch around its reference city instead of a full band.
const FALLBACK_LAT_RADIUS = 5
// Dot sizes are designed at the sidebar panel's width; wider maps scale
// them up in proportion.
const BASE_WIDTH = 232
// Wide renders (the full-size map) get inline zone labels.
const LABEL_MIN_WIDTH = 600
// How long a departed zone lingers (at scale 0) so its shrink-out can
// play; slightly longer than the 0.5s transform transition.
const EXIT_MS = 600
// Radiating ping when a clip event lands on a zone's dot; matches the
// tzmap-ping keyframes duration below.
const PING_MS = 1500
// Matches the persona-bubble quote callout lifetime.
const CALLOUT_MS = 6000

const LAND = feature(landTopo, landTopo.objects.land)

// ISO 3166-1 numeric id -> country features, for clipping a zone's band
// to the countries that actually use it (ids come from timezoneGeo).
// A list per id: the 50m atlas repeats an id across features (Australia
// and Ashmore and Cartier Islands are both 036), and a plain map would
// keep only the last one — the clip is the union of all of them.
const COUNTRIES = new Map()
feature(countriesTopo, countriesTopo.objects.countries).features.forEach(
  (country) => {
    const id = String(country.id)
    if (!COUNTRIES.has(id)) COUNTRIES.set(id, [])
    COUNTRIES.get(id).push(country)
  }
)

// Exterior ring wound clockwise — d3-geo's convention for spherical
// polygons smaller than a hemisphere (the reverse winding would select
// the rest of the globe).
const bandPolygon = (lng, south, north) => ({
  type: 'Polygon',
  coordinates: [
    [
      [lng - BAND_DEGREES / 2, south],
      [lng - BAND_DEGREES / 2, north],
      [lng + BAND_DEGREES / 2, north],
      [lng + BAND_DEGREES / 2, south],
      [lng - BAND_DEGREES / 2, south],
    ],
  ],
})

const zoneLabel = (timezoneId) =>
  timezoneId === 'unknown' ? 'Unknown' : timezoneId.replace(/_/g, ' ')

const cityLabel = (timezoneId) => timezoneId.split('/').pop().replace(/_/g, ' ')

// Shared 2d context for measuring label text without DOM layout.
let measureContext = null
const measureLabel = (text, font) => {
  if (!measureContext) {
    measureContext = document.createElement('canvas').getContext('2d')
  }
  measureContext.font = font
  return measureContext.measureText(text).width
}

// Fold a zone's cohort combos into the shared persona partition; only
// personas present in the zone are listed, in the standard GUEST, NEW,
// USER, POWER order (the pies read in this order; the tooltip re-sorts
// its copy by count).
const personaRows = (cohorts) =>
  PERSONAS.map(({ key, color, match }) => ({
    key,
    color,
    count: (cohorts || [])
      .filter((cohort) => match(cohort))
      .reduce((sum, { count }) => sum + count, 0),
  })).filter(({ count }) => count > 0)

// Persona pie slices for a zone's dot, same footprint as the solid dot.
const pieSlices = (zone) => {
  const pie = d3
    .pie()
    .value(({ count }) => count)
    .sort(null)
  const arc = d3.arc().innerRadius(0).outerRadius(zone.r)
  return pie(personaRows(zone.cohorts)).map((slice) => ({
    d: arc(slice),
    color: slice.data.color,
    key: slice.data.key,
  }))
}

const ZoneTooltipBody = ({ timezoneId, count, cohorts }) => (
  <Box sx={{ minWidth: 110 }}>
    <Typography variant="caption" fontWeight={700} display="block">
      {zoneLabel(timezoneId)} · {count}
    </Typography>
    {[...personaRows(cohorts)]
      .sort((a, b) => b.count - a.count)
      .map(({ key, color, count: personaCount }) => (
        <Box
          key={key}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: color,
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" sx={{ flex: 1 }}>
            {key}
          </Typography>
          <Typography variant="caption" fontWeight={700}>
            {personaCount}
          </Typography>
        </Box>
      ))}
  </Box>
)

// Live mic-on users placed by their device timezone: a one-hour-wide
// band (deeper with more users) clipped to the countries that use the
// zone, plus a dot at the zone's tz-database reference city sized by
// count. Hovering a band or dot shows the
// zone's total and persona breakdown. Fills whatever width it's given;
// `preview` marks the clickable sidebar instance (overline header, no
// persona-pie toggle).
const TimezoneMap = ({ timezones, preview = false, subscribeSparkles }) => {
  const theme = useTheme()
  const containerRef = useRef(null)
  // clipPath ids are document-global; two map instances share the page.
  const uid = useRef(`tzmap-${Math.random().toString(36).slice(2, 8)}`).current
  const [width, setWidth] = useState(0)
  const [hover, setHover] = useState(null)
  const [pies, setPies] = useState(false)

  useEffect(() => {
    const element = containerRef.current
    setWidth(element.offsetWidth)
    const observer = new ResizeObserver((entries) => {
      const next = Math.round(entries[0].contentRect.width)
      setWidth((prev) => (Math.abs(prev - next) > 1 ? next : prev))
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const { projection, path, height, landPath } = useMemo(() => {
    if (!width) return {}
    const naturalEarth = d3
      .geoNaturalEarth1()
      .fitWidth(width, { type: 'Sphere' })
    const geoPath = d3.geoPath(naturalEarth)
    const [, [, sphereBottom]] = geoPath.bounds({ type: 'Sphere' })
    return {
      projection: naturalEarth,
      path: geoPath,
      height: Math.ceil(sphereBottom),
      landPath: geoPath(LAND),
    }
  }, [width])

  // Fold legacy alias ids (US/Pacific, Asia/Calcutta, ...) into their
  // canonical zone so one region never renders as two stacked zones
  // splitting the count between them.
  const mergedTimezones = useMemo(() => {
    if (!timezones) return null
    const byId = new Map()
    timezones.forEach(({ timezone_id: rawId, count, cohorts }) => {
      const timezoneId = TIMEZONE_ALIASES[rawId] || rawId
      const zone = byId.get(timezoneId)
      if (!zone) {
        byId.set(timezoneId, {
          timezone_id: timezoneId,
          count,
          cohorts: (cohorts || []).map((cohort) => ({ ...cohort })),
        })
        return
      }
      zone.count += count
      ;(cohorts || []).forEach((cohort) => {
        const match = zone.cohorts.find(
          (existing) =>
            existing.user_type === cohort.user_type &&
            existing.account_age === cohort.account_age &&
            existing.usage_tier === cohort.usage_tier
        )
        if (match) match.count += cohort.count
        else zone.cohorts.push({ ...cohort })
      })
    })
    return [...byId.values()]
  }, [timezones])

  const zones = useMemo(() => {
    if (!mergedTimezones || !projection) return null
    const maxCount = Math.max(1, ...mergedTimezones.map(({ count }) => count))
    const sizeFactor = Math.sqrt(width / BASE_WIDTH)
    const radius = d3
      .scaleSqrt()
      .domain([1, maxCount])
      .range([3 * sizeFactor, 9 * sizeFactor])
    return mergedTimezones.map(
      ({ timezone_id: timezoneId, count, cohorts }) => {
        const coords = TIMEZONE_COORDS[timezoneId]
        const rows = personaRows(cohorts)
        const dominant = rows.reduce(
          (top, row) => (row.count > top.count ? row : top),
          rows[0] || { color: null }
        )
        // The sidebar preview keeps the simple full-height bands; country
        // clipping only reads at the full-size map's scale.
        const countryIds =
          coords && !preview ? coords[2].filter((id) => COUNTRIES.has(id)) : []
        const bandShape = () => {
          const [lat, lng] = coords
          if (preview) return bandPolygon(lng, PREVIEW_SOUTH, PREVIEW_NORTH)
          if (countryIds.length > 0)
            return bandPolygon(lng, BAND_SOUTH, BAND_NORTH)
          return bandPolygon(
            lng,
            lat - FALLBACK_LAT_RADIUS,
            lat + FALLBACK_LAT_RADIUS
          )
        }
        return {
          timezoneId,
          count,
          cohorts,
          band: coords ? path(bandShape()) : null,
          // Countries the zone covers (per the tz database), used to clip
          // the band so it doesn't wash over neighbors and oceans.
          countryIds: countryIds.length > 0 ? countryIds : null,
          point: coords ? projection([coords[1], coords[0]]) : null,
          r: radius(count),
          // The zone's biggest persona, used to tint the band in pie mode.
          dominantColor: dominant.color,
          // More users deepen the band. Log scale: a linear ramp spends
          // all its contrast on the busiest zone, leaving 28 users vs 1
          // indistinguishable when some zone has hundreds.
          bandOpacity: 0.06 + 0.12 * (Math.log1p(count) / Math.log1p(maxCount)),
        }
      }
    )
  }, [mergedTimezones, projection, path, width, preview])

  // Enter/exit animation state. A zone's dot first paints at scale 0
  // and flips to full a frame later so it grows in; departed zones
  // linger briefly with a scale-0 target so the shrink plays before
  // removal. Bands don't scale — their FILL radiates: each zone's band
  // is clipped by a rect that wipes open north/south from the dot, and
  // a fill change bumps the zone's wipe generation to replay the wipe
  // with the new fill over an underlay holding the old one.
  const prevLocatedRef = useRef(new Map())
  const bandGensRef = useRef(new Map())
  const [arrivedIds, setArrivedIds] = useState(() => new Set())
  const [exiting, setExiting] = useState([])
  const [bandWipes, setBandWipes] = useState(() => new Map())

  useEffect(() => {
    if (!zones) return undefined
    const previous = prevLocatedRef.current
    const current = new Map(
      zones.filter(({ point }) => point).map((zone) => [zone.timezoneId, zone])
    )
    const departed = [...previous.values()].filter(
      ({ timezoneId }) => !current.has(timezoneId)
    )
    prevLocatedRef.current = current

    // Departed zones join the exiting list; zones that came back while
    // still exiting resume as live.
    setExiting((old) => [
      ...old.filter(
        ({ timezoneId }) =>
          !current.has(timezoneId) &&
          !departed.some((zone) => zone.timezoneId === timezoneId)
      ),
      ...departed,
    ])
    if (departed.length > 0) {
      setTimeout(() => {
        setExiting((old) =>
          old.filter(
            ({ timezoneId }) =>
              !departed.some((zone) => zone.timezoneId === timezoneId)
          )
        )
      }, EXIT_MS)
      // An exiting element loses pointer events without firing
      // mouseleave, so drop a tooltip pinned to a departed zone.
      setHover((old) => (old && !current.has(old.zone.timezoneId) ? null : old))
    }

    // New zones replay the wipe, as do zones whose own population
    // actually changed (or whose dominant persona recolors the band in
    // pie mode). Deliberately NOT bandOpacity: it's scaled to maxCount,
    // so a spike in one zone would shift every band's opacity and
    // replay every wipe on each refresh — those bystander opacity
    // shifts just ease via the fill-opacity transition instead.
    const wipeUpdates = []
    current.forEach((zone, id) => {
      const before = previous.get(id)
      if (
        !before ||
        before.count !== zone.count ||
        before.dominantColor !== zone.dominantColor
      ) {
        const gen = (bandGensRef.current.get(id) || 0) + 1
        bandGensRef.current.set(id, gen)
        wipeUpdates.push({
          id,
          gen,
          prev: before
            ? { opacity: before.bandOpacity, color: before.dominantColor }
            : null,
        })
      }
    })
    if (wipeUpdates.length > 0) {
      setBandWipes((old) => {
        const next = new Map(old)
        wipeUpdates.forEach(({ id, gen, prev }) => next.set(id, { gen, prev }))
        return next
      })
      // Drop the old-fill underlays once their wipes have finished
      // (unless a newer wipe took over in the meantime).
      setTimeout(() => {
        setBandWipes((old) => {
          const next = new Map(old)
          wipeUpdates.forEach(({ id, gen }) => {
            const entry = next.get(id)
            if (entry && entry.gen === gen && entry.prev) {
              next.set(id, { ...entry, prev: null })
            }
          })
          return next
        })
      }, EXIT_MS)
    }

    // Flip arriving dots to full scale one frame after their scale-0
    // paint so the grow-in transition actually runs.
    const frame = requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        setArrivedIds((old) => {
          const ids = new Set(current.keys())
          const unchanged =
            ids.size === old.size && [...ids].every((id) => old.has(id))
          return unchanged ? old : ids
        })
      )
    )
    return () => cancelAnimationFrame(frame)
  }, [zones])

  // Clip events: create radiates a ping from the zone's dot; text adds a
  // speech-bubble callout on the full-size map. Position is read from the
  // current located-zones map at fire time. A zone that isn't on the map
  // (unknown timezone, or the clipper already dropped off) skips both.
  const [pings, setPings] = useState([])
  const [callouts, setCallouts] = useState([])

  useEffect(() => {
    if (!subscribeSparkles) return undefined
    return subscribeSparkles((event) => {
      const timezoneId = TIMEZONE_ALIASES[event.timezoneId] || event.timezoneId
      const zone = prevLocatedRef.current.get(timezoneId)
      if (!zone || !zone.point) return

      if (event.type === 'create') {
        const pingId = `${event.type}-${event.id}-${Date.now()}`
        setPings((old) => [
          ...old,
          {
            id: pingId,
            x: zone.point[0],
            y: zone.point[1],
            r: Math.max(zone.r, 4),
            personaKey: event.personaKey,
          },
        ])
        setTimeout(
          () => setPings((old) => old.filter((ping) => ping.id !== pingId)),
          PING_MS
        )
      }

      // The mini map keeps the pulse but not the quote, which would be too
      // large for the panel. As on the persona bubbles, titles never appear:
      // event.text is populated only from best_quote.
      if (preview || !event.text) return
      const calloutId = `${event.type}-${event.id}`
      setCallouts((old) => [
        // Keep at most 3 on screen; oldest gives way.
        ...old.filter((callout) => callout.id !== calloutId).slice(-2),
        {
          id: calloutId,
          timezoneId,
          text: event.text,
        },
      ])
      setTimeout(() => {
        setCallouts((old) => old.filter((callout) => callout.id !== calloutId))
      }, CALLOUT_MS)
    })
  }, [preview, subscribeSparkles])

  // Projected outlines for every country referenced by a visible zone
  // (live or shrinking out), rendered as clip paths over the bands.
  const countryPaths = useMemo(() => {
    if (!path) return {}
    const paths = {}
    const collect = (zone) =>
      (zone.countryIds || []).forEach((id) => {
        if (!(id in paths)) paths[id] = COUNTRIES.get(id).map(path)
      })
    ;(zones || []).forEach(collect)
    exiting.forEach(collect)
    return paths
  }, [zones, exiting, path])

  // Greedy label placement for the full-size map: dots are obstacles,
  // busiest zones claim a spot first, and each label tries right, left,
  // above, then below of its dot — the first position that neither
  // collides with anything placed nor runs off the map wins. A zone
  // whose every candidate collides goes label-less (its dot and tooltip
  // still carry it), which is what thins out the US-east/Europe pileup.
  const placedLabels = useMemo(() => {
    if (!zones || width < LABEL_MIN_WIDTH) return []
    const font = `12px ${theme.typography.fontFamily}`
    const labelH = 14
    const pad = 2
    const locatedZones = zones.filter(({ point }) => point)
    const boxes = locatedZones.map(({ point, r }) => ({
      x: point[0] - r,
      y: point[1] - r,
      w: r * 2,
      h: r * 2,
    }))
    const collides = (box) =>
      boxes.some(
        (other) =>
          box.x < other.x + other.w + pad &&
          box.x + box.w + pad > other.x &&
          box.y < other.y + other.h + pad &&
          box.y + box.h + pad > other.y
      )
    const labels = []
    ;[...locatedZones]
      .sort((a, b) => b.count - a.count)
      .forEach((zone) => {
        const text = `${cityLabel(zone.timezoneId)} · ${zone.count}`
        const w = measureLabel(text, font)
        const [px, py] = zone.point
        const gap = zone.r + 5
        const candidates = [
          {
            x: px + gap,
            y: py,
            anchor: 'start',
            box: { x: px + gap, y: py - labelH / 2, w, h: labelH },
          },
          {
            x: px - gap,
            y: py,
            anchor: 'end',
            box: { x: px - gap - w, y: py - labelH / 2, w, h: labelH },
          },
          {
            x: px,
            y: py - gap - 4,
            anchor: 'middle',
            box: { x: px - w / 2, y: py - gap - 4 - labelH / 2, w, h: labelH },
          },
          {
            x: px,
            y: py + gap + 4,
            anchor: 'middle',
            box: { x: px - w / 2, y: py + gap + 4 - labelH / 2, w, h: labelH },
          },
        ]
        const spot = candidates.find(
          ({ box }) => box.x >= 0 && box.x + box.w <= width && !collides(box)
        )
        if (!spot) return
        boxes.push(spot.box)
        labels.push({ zone, text, x: spot.x, y: spot.y, anchor: spot.anchor })
      })
    return labels
  }, [zones, width, theme])

  const located = zones ? zones.filter(({ point }) => point) : []
  const calloutGroups = callouts.reduce((groups, callout) => {
    const zone = located.find(
      ({ timezoneId }) => timezoneId === callout.timezoneId
    )
    if (!zone) return groups
    const group = groups.get(callout.timezoneId) || { zone, callouts: [] }
    group.callouts.push(callout)
    groups.set(callout.timezoneId, group)
    return groups
  }, new Map())
  const unlocatedUsers = zones
    ? zones
        .filter(({ point }) => !point)
        .reduce((sum, { count }) => sum + count, 0)
    : 0

  // Follow-the-mouse tooltip, same pattern as the other panels. Pointer
  // coords come back in visual (post-transform) pixels; divide by the
  // wallboard scale to get back to local coordinates.
  const moveHover = (zone) => (event) => {
    const container = containerRef.current
    const rect = container.getBoundingClientRect()
    const scale = rect.width / container.offsetWidth || 1
    setHover({
      zone,
      x: (event.clientX - rect.left) / scale,
      y: (event.clientY - rect.top) / scale,
    })
  }
  const hoverProps = (zone) => ({
    onMouseEnter: moveHover(zone),
    onMouseMove: moveHover(zone),
    onMouseLeave: () => setHover(null),
  })

  const hoverFlip = hover && hover.x > width * 0.7

  const hoveredId = hover ? hover.zone.timezoneId : null
  // While a zone is hovered, everything else fades back.
  const dimmed = (zone) => hoveredId !== null && zone.timezoneId !== hoveredId

  // Live zones plus departed ones playing their shrink-out.
  const renderZones = [
    ...located,
    ...exiting.map((zone) => ({ ...zone, exiting: true })),
  ]
  const zoneScale = (zone) =>
    zone.exiting || !arrivedIds.has(zone.timezoneId) ? 0 : 1

  const renderDot = (zone) =>
    pies ? (
      <g
        key={`dot-${zone.timezoneId}`}
        opacity={dimmed(zone) ? 0.35 : 1}
        style={{
          transform: `translate(${zone.point[0]}px, ${
            zone.point[1]
          }px) scale(${zoneScale(zone)})`,
          transition: 'transform 0.5s ease, opacity 0.15s',
          pointerEvents: zone.exiting ? 'none' : undefined,
        }}
        {...(zone.exiting ? {} : hoverProps(zone))}
      >
        {pieSlices(zone).map(({ d, color, key }) => (
          <path key={key} d={d} fill={color} />
        ))}
        {/* One outline around the whole pie, no slice seams. */}
        <circle
          r={zone.r}
          fill="none"
          stroke={theme.palette.background.paper}
          strokeWidth={1}
        />
      </g>
    ) : (
      <circle
        key={`dot-${zone.timezoneId}`}
        cx={zone.point[0]}
        cy={zone.point[1]}
        r={zone.r * zoneScale(zone)}
        fill={theme.palette.primary.main}
        fillOpacity={0.85}
        stroke={theme.palette.background.paper}
        strokeWidth={1}
        opacity={dimmed(zone) ? 0.35 : 1}
        style={{
          transition: 'r 0.5s ease, cx 0.4s, cy 0.4s, opacity 0.15s',
          pointerEvents: zone.exiting ? 'none' : undefined,
        }}
        {...(zone.exiting ? {} : hoverProps(zone))}
      />
    )

  return (
    <>
      {/* The sidebar preview echoes the "MICS ON NOW" overline; the
          full-size panel uses the standard panel header. */}
      <Box
        sx={{
          display: 'flex',
          // Preview aligns on the overline's baseline (same fix as the
          // "Mics on now" dot): centering uses the full line box, whose
          // descender space floats the icon high against the caps. The
          // full-size header keeps centering — its pie-toggle button has
          // no text baseline and would sit oddly on one.
          alignItems: preview ? 'baseline' : 'center',
          justifyContent: preview ? 'center' : 'flex-start',
          gap: 0.75,
          mb: preview ? 0.5 : 1,
        }}
      >
        {preview ? (
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: 2 }}
          >
            Clipping from
          </Typography>
        ) : (
          <Typography variant="subtitle1" fontWeight={700}>
            Clipping from
          </Typography>
        )}
        <Tooltip
          title={`Users on right now, grouped by their device's timezone. Each highlight is a whole timezone — the dot sits at the zone's reference city, so all of US Central shows at Chicago. Hover a zone for its persona breakdown.${
            preview ? ' Click the panel to jump to the full-size map.' : ''
          }`}
          placement="right"
        >
          <InfoOutlinedIcon
            sx={{
              fontSize: preview ? 14 : 16,
              color: 'text.secondary',
              cursor: 'default',
              // Baseline alignment puts the icon's bottom on the text
              // baseline, but at 14px the icon is taller than the caps,
              // so its center overshoots; drop it back to cap-center.
              position: preview ? 'relative' : undefined,
              top: preview ? '2.5px' : undefined,
            }}
          />
        </Tooltip>
        {!preview && (
          <>
            <Box sx={{ flex: 1 }} />
            <Tooltip
              title={pies ? 'Solid dots' : 'Split each dot by persona'}
              placement="left"
            >
              <IconButton
                size="small"
                color={pies ? 'primary' : 'default'}
                onClick={() => setPies((value) => !value)}
              >
                <PieChartIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      {/* flex/justify only bite inside a flex-column parent (the preview
          panel), centering the map in the panel's spare height. */}
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          width: '100%',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        {zones ? (
          <>
            <svg width={width} height={height} style={{ display: 'block' }}>
              <path
                d={landPath}
                fill={theme.palette.text.secondary}
                fillOpacity={0.18}
              />
              {/* The wipe animations scale the CLIP rect, not the band
                  shape, so the fill sweeps north/south from the dot
                  while the band's geometry stays put. */}
              <style>{`
                @keyframes tzmap-wipe {
                  from { transform: scaleY(0); }
                  to { transform: scaleY(1); }
                }
                @keyframes tzmap-unwipe {
                  from { transform: scaleY(1); }
                  to { transform: scaleY(0); }
                }
                @keyframes tzmap-ping {
                  from { transform: scale(1); opacity: 0.9; }
                  to { transform: scale(3.5); opacity: 0; }
                }
              `}</style>
              {renderZones.map((zone) => {
                const wipe = bandWipes.get(zone.timezoneId)
                const clipId = `${uid}-${zone.timezoneId.replace(
                  /[^a-zA-Z0-9]/g,
                  '-'
                )}`
                const bandFill = (color) =>
                  pies && color ? color : theme.palette.primary.main
                // Union of the zone's countries; the band only paints
                // where it overlaps them.
                const countryClip = (zone.countryIds || [])
                  .flatMap((id) => countryPaths[id] || [])
                  .filter(Boolean)
                return (
                  <g
                    key={`band-${zone.timezoneId}`}
                    opacity={dimmed(zone) ? 0.3 : 1}
                    style={{
                      transition: 'opacity 0.15s',
                      pointerEvents: zone.exiting ? 'none' : undefined,
                    }}
                    {...(zone.exiting ? {} : hoverProps(zone))}
                  >
                    {countryClip.length > 0 && (
                      <clipPath id={`${clipId}-country`}>
                        {countryClip.map((d, index) => (
                          <path key={index} d={d} />
                        ))}
                      </clipPath>
                    )}
                    <clipPath id={clipId}>
                      <rect
                        key={wipe ? wipe.gen : 0}
                        x={0}
                        y={0}
                        width={width}
                        height={height}
                        style={{
                          transformOrigin: `0px ${zone.point[1]}px`,
                          animation: `${
                            zone.exiting ? 'tzmap-unwipe' : 'tzmap-wipe'
                          } 0.5s ease forwards`,
                        }}
                      />
                    </clipPath>
                    <g
                      clipPath={
                        countryClip.length > 0
                          ? `url(#${clipId}-country)`
                          : undefined
                      }
                    >
                      {/* The old fill sits underneath while a wipe plays,
                          masked to the wipe's complement (black rect runs
                          the same animation as the clip rect) so the new
                          fill REPLACES it at the front — stacking the two
                          translucent fills would read darker than either
                          and then snap when the underlay drops. */}
                      {wipe && wipe.prev && !zone.exiting && (
                        <>
                          <mask id={`${clipId}-old`}>
                            <rect
                              x={0}
                              y={0}
                              width={width}
                              height={height}
                              fill="white"
                            />
                            <rect
                              key={wipe.gen}
                              x={0}
                              y={0}
                              width={width}
                              height={height}
                              fill="black"
                              style={{
                                transformOrigin: `0px ${zone.point[1]}px`,
                                animation: 'tzmap-wipe 0.5s ease forwards',
                              }}
                            />
                          </mask>
                          <path
                            d={zone.band}
                            fill={bandFill(wipe.prev.color)}
                            fillOpacity={wipe.prev.opacity}
                            mask={`url(#${clipId}-old)`}
                          />
                        </>
                      )}
                      {/* Keyed by wipe generation: a wipe remounts the
                          path so it paints straight at the new fill, while
                          bystander opacity shifts (maxCount moved) keep
                          the node and ease via the transition. */}
                      <path
                        key={wipe ? wipe.gen : 0}
                        d={zone.band}
                        fill={bandFill(zone.dominantColor)}
                        fillOpacity={zone.bandOpacity}
                        clipPath={`url(#${clipId})`}
                        style={{
                          transition: 'fill 0.4s ease, fill-opacity 0.4s ease',
                        }}
                      />
                    </g>
                  </g>
                )
              })}
              {/* Paint order: unhovered dots, then labels, then the
                  hovered dot — so the highlighted dot sits above even
                  the neighboring text labels. */}
              {renderZones
                .filter(({ timezoneId }) => timezoneId !== hoveredId)
                .map(renderDot)}
              {placedLabels.map(({ zone, text, x, y, anchor }) => (
                <text
                  key={`label-${zone.timezoneId}`}
                  x={x}
                  y={y}
                  textAnchor={anchor}
                  dominantBaseline="middle"
                  opacity={zoneScale(zone) === 0 ? 0 : dimmed(zone) ? 0.25 : 1}
                  style={{
                    fontSize: 12,
                    pointerEvents: 'none',
                    transition: 'opacity 0.3s',
                  }}
                  fill={theme.palette.text.primary}
                >
                  {text}
                </text>
              ))}
              {renderZones
                .filter(({ timezoneId }) => timezoneId === hoveredId)
                .map(renderDot)}
              {pings.map(({ id, x, y, r, personaKey }) => {
                // In pie mode the ring takes the clipper's persona
                // color (matching the dot slices); solid-dot mode keeps
                // the primary accent. Render-time choice so toggling
                // pies recolors in-flight pings too.
                const persona = pies
                  ? PERSONAS.find(({ key }) => key === personaKey)
                  : null
                return (
                  <g
                    key={id}
                    style={{ transform: `translate(${x}px, ${y}px)` }}
                    pointerEvents="none"
                  >
                    <circle
                      r={r}
                      fill="none"
                      stroke={
                        persona ? persona.color : theme.palette.primary.main
                      }
                      strokeWidth={2}
                      style={{
                        transformOrigin: '0 0',
                        animation: `tzmap-ping ${PING_MS}ms ease-out forwards`,
                      }}
                    />
                  </g>
                )
              })}
            </svg>

            {zones.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1.5, textAlign: 'center' }}
              >
                No one is on right now.
              </Typography>
            )}
            {unlocatedUsers > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: 'block', textAlign: 'center' }}
              >
                +{unlocatedUsers} with an unknown timezone
              </Typography>
            )}

            {[...calloutGroups.values()].map(
              ({ zone, callouts: zoneCallouts }) => (
                <Box
                  key={zone.timezoneId}
                  sx={{
                    position: 'absolute',
                    left: zone.point[0],
                    top: zone.point[1] - zone.r - 14,
                    transform: 'translate(-50%, -100%)',
                    display: 'flex',
                    flexDirection: 'column-reverse',
                    alignItems: 'center',
                    gap: 1,
                    pointerEvents: 'none',
                    zIndex: 2,
                  }}
                >
                  {zoneCallouts.map(({ id, text }) => (
                    <Box
                      key={id}
                      sx={{
                        position: 'relative',
                        width: 'max-content',
                        maxWidth: 220,
                        bgcolor: 'rgba(0, 0, 0, 0.85)',
                        color: 'white',
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 3,
                        fontSize: 12,
                        fontStyle: 'italic',
                        textAlign: 'center',
                        transformOrigin: '50% 100%',
                        animation: `tzmap-callout ${CALLOUT_MS}ms ease forwards`,
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: '100%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          borderLeft: '7px solid transparent',
                          borderRight: '7px solid transparent',
                          borderTop: '8px solid rgba(0, 0, 0, 0.85)',
                        },
                        '@keyframes tzmap-callout': {
                          '0%': { opacity: 0, transform: 'scale(0.2)' },
                          '5%': { opacity: 1, transform: 'scale(1.05)' },
                          '7%': { transform: 'scale(1)' },
                          '85%': { opacity: 1, transform: 'scale(1)' },
                          '100%': { opacity: 0, transform: 'scale(1)' },
                        },
                      }}
                    >
                      “{text}”
                    </Box>
                  ))}
                </Box>
              )
            )}

            {hover && (
              <Box
                sx={{
                  position: 'absolute',
                  left: hoverFlip ? 'auto' : `${hover.x + 12}px`,
                  right: hoverFlip ? `${width - hover.x + 12}px` : 'auto',
                  top: `${hover.y - 12}px`,
                  pointerEvents: 'none',
                  bgcolor: 'rgba(0, 0, 0, 0.8)',
                  color: 'white',
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 1,
                  whiteSpace: 'nowrap',
                  zIndex: 1,
                }}
              >
                <ZoneTooltipBody {...hover.zone} />
              </Box>
            )}
          </>
        ) : (
          <Typography variant="caption" color="error">
            Failed to load the live count.
          </Typography>
        )}
      </Box>
    </>
  )
}

export default TimezoneMap
